import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { AbleConfigSchema, AbleConfigSchemaProperty } from '../interfaces/ableConfigSchema';
import { AbleService } from '../services/able.service';

// Resolves "#/definitions/<name>" $ref pointers and collapses oneOf/anyOf
// branches into a single renderable shape so JsonSchemaForm (which has no
// $ref/oneOf support) can render array/object items.
const resolveSchemaRefs = (schema: AbleConfigSchema): AbleConfigSchema => {
  const definitions = schema.definitions || {};

  // Prefer array > object > any typed branch so e.g. best_plate_selection
  // (oneOf: [array, single object]) renders as an array.
  const pickBranch = (branches: AbleConfigSchemaProperty[]): AbleConfigSchemaProperty => {
    return branches.find((b) => b.type === 'array') || branches.find((b) => b.type) || branches[0];
  };

  const resolve = (node: AbleConfigSchemaProperty, seen: Set<string>): AbleConfigSchemaProperty => {
    // 1. Inline $ref by merging the referenced definition into the node.
    if (node.$ref && !seen.has(node.$ref)) {
      const target = definitions[node.$ref.replace('#/definitions/', '')];
      if (target) {
        const { $ref, ...rest } = node;
        return resolve({ ...target, ...rest }, new Set(seen).add($ref));
      }
    }

    // 2. Collapse oneOf/anyOf into the node when it has no concrete type.
    const branches = !node.type ? node.oneOf || node.anyOf : undefined;
    if (branches?.length) {
      const rest = { ...node };
      // Drop oneOf/anyOf so the resolved node represents only the picked branch
      // (otherwise the original variants would leak into the output schema).
      delete rest.oneOf;
      delete rest.anyOf;
      return resolve({ ...pickBranch(branches), ...rest }, seen);
    }

    // 3. Recurse into children.
    return {
      ...node,
      ...(node.items && { items: resolve(node.items, seen) }),
      ...(node.properties && {
        properties: Object.fromEntries(Object.entries(node.properties).map(([k, v]) => [k, resolve(v, seen)])),
      }),
      ...(typeof node.additionalProperties === 'object' && {
        additionalProperties: resolve(node.additionalProperties, seen),
      }),
    };
  };

  return {
    ...schema,
    properties: Object.fromEntries(Object.entries(schema.properties).map(([k, v]) => [k, resolve(v, new Set())])),
  };
};

export const useAbleConfigSchemaVersionsQuery = (): UseQueryResult<string[]> => {
  return useQuery<string[]>({
    queryKey: ['able-config-schema-versions'],
    queryFn: async () => {
      const versions = await AbleService.getAbleConfigSchemaVersions();
      // Sort descending using natural (numeric-aware) comparison so e.g. v2.15.14 > v2.15.10 > v2.13.20.
      return [...versions].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
    },
    staleTime: Infinity, // List doesn't change often
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
  });
};

export const useAbleConfigSchemaQuery = (version: string | undefined): UseQueryResult<AbleConfigSchema> => {
  return useQuery<AbleConfigSchema>({
    queryKey: ['able-config-schema', version],
    queryFn: async () => {
      const raw = await AbleService.getAbleConfigSchema(version as string);
      return resolveSchemaRefs(raw);
    },
    staleTime: Infinity, // Schema doesn't change often
    cacheTime: 1000 * 60 * 60, // Cache for 1 hour
    enabled: !!version,
  });
};
